import {
  Alert,
  Button,
  Col,
  Modal,
  ProgressBar,
  Row,
  Table,
} from 'react-bootstrap';
import { useEffect, useState } from 'react';
import submitApplicationSignal, {
  applicationDraftSignal,
} from 'signals/SubmitApplication.signal';
import api from 'api/api';
import {
  handleAttachFileForOcr,
  handleChangeToDigitalForm,
  handleChangeToDocumentUpload,
  handleDownloadDocument,
  nextStep,
  prevStep,
  toggleApplicationFormTypeWarningModal,
} from '../SubmitApplications.helpers';
import OCRRender from 'components/global/OCR/OCRRender';
import ModalWrapper from 'components/global/ModalWrapper/ModalWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { downloadIcon, warningIcon } from '../../Constant/Icons';
import { isFileTypeValidForPreview } from 'utils/checkFileTypeForPreview';
import FilePreviewButton from '../../utils/FilesAndDocuments/PreviewButton/FilePreviewButton';
import { ocrSignal } from '../../OCR/ocr.signal';
import { getOcrCategoriesToRender } from '../../OCR/ocr.helpers';
import { onTestMode } from '../../../../libs/functions/global.functions';
import CloseModalButton from 'components/global/Constant/CloseModalButton';
import $appSettings from 'signals/AppSettings.signal';

const ConfirmApplicationFormVersionModal = () => {
  const { isUsingOcr, applicationFormWarningModalVisible } =
    submitApplicationSignal.value;

    return (
    <ModalWrapper
      show={applicationFormWarningModalVisible}
      centered
      onHide={toggleApplicationFormTypeWarningModal}
    >
      <Modal.Header className="justify-content-end">
        <CloseModalButton
          onCloseModal={toggleApplicationFormTypeWarningModal}
        />
      </Modal.Header>
      <Modal.Body>
        <p>
          Your progress for the application form will be lost. Are you sure you
          want change to{' '}
          {isUsingOcr ? 'a digital form' : 'an uploaded document'}?
        </p>
      </Modal.Body>
      <Modal.Footer className="justify-content-end">
        <Button
          variant="outline-secondary"
          className="mr-16"
          onClick={toggleApplicationFormTypeWarningModal}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (isUsingOcr) {
              handleChangeToDigitalForm();
            } else {
              handleChangeToDocumentUpload();
            }
          }}
        >
          Continue
        </Button>
      </Modal.Footer>
    </ModalWrapper>
  );
};

export const SubmitApplicationsOCR = () => {
  const {
    fundingProgram,
    fundingProgramId,
    ocrFileIsProcessing,
    ocrModalVisible,
    targetDocumentName,
    ocrFile,
    ocrFileSignedUrl,
    ocrFileType,
    ocrFileUuid,
    ocrIntakeAlert,
    isUsingOcr,
  } = submitApplicationSignal.value;
  const { selectedDraft } = applicationDraftSignal.value;
  const { DOCUMENT_TYPES } = $appSettings.value.constants;

  // Ensure fundingProgram has supportingDocuments, fetch if missing
  const [localFundingProgram, setLocalFundingProgram] = useState(fundingProgram);
  const ocrAvailable = !!(
    localFundingProgram?.ocrModelKey || fundingProgram?.ocrModelKey
  );

  useEffect(() => {
    if (fundingProgram && fundingProgram.supportingDocuments) {
      setLocalFundingProgram(fundingProgram);
    } else if (fundingProgramId && (!fundingProgram || !fundingProgram.supportingDocuments)) {
      // Fetch funding program with supportingDocuments if missing
      api.get({
        path: `/fundingPrograms?id=${fundingProgramId}&include[supportingDocuments]=true&include[referenceProgram]=true`,
      }).then((fp) => {
        const merged = {
          ...fp,
          referenceProgram: fp?.referenceProgram || fundingProgram?.referenceProgram,
        };
        setLocalFundingProgram(merged);
        submitApplicationSignal.update({ fundingProgram: merged });
      }).catch(() => {
        // Keep existing fundingProgram if fetch fails
        setLocalFundingProgram(fundingProgram);
      });
    } else {
      setLocalFundingProgram(fundingProgram);
    }
  }, []);

  /**
   * Keep `modelId` aligned with the selected funding program so category order (and related OCR
   * behavior) works after “Continue” on the form-type modal, draft resume, or late-loaded program.
   */
  useEffect(() => {
    const key = localFundingProgram?.ocrModelKey || fundingProgram?.ocrModelKey;
    if (!key) return;
    const { modelId, parsedOcrData } = ocrSignal.value;
    if (modelId === key) return;
    ocrSignal.update({
      modelId: key,
      sortedCategories: getOcrCategoriesToRender(parsedOcrData, key),
    });
  }, [
    localFundingProgram?.ocrModelKey,
    localFundingProgram?.id,
    fundingProgram?.ocrModelKey,
    fundingProgram?.id,
  ]);

  const supportingDocuments = localFundingProgram?.supportingDocuments || [];
  const applicationTemplates = supportingDocuments.filter(
    (sd) => sd.documentType === DOCUMENT_TYPES.applicationTemplate
  );

  const { parsedOcrData } = ocrSignal.value;

  return (
    <>
      <ConfirmApplicationFormVersionModal />
      <Row>
        <Col>
          <h4>Application Form</h4>
          <p>
            Upload the full application or only the sections you have completed
            on paper. Remaining fields can be filled in digitally below.
          </p>
        </Col>
      </Row>
      <Row className="mb-32">
        <Col>
          <Button
            className="w-100"
            disabled={!ocrAvailable}
            variant={isUsingOcr ? 'secondary' : 'outline-secondary'}
            onClick={() => {
              // Already on OCR upload path, or OCR not available.
              if (!ocrAvailable || isUsingOcr) return;
              toggleApplicationFormTypeWarningModal();
            }}
          >
            Upload a Document
          </Button>
        </Col>
        <Col>
          <Button
            className="w-100"
            variant={isUsingOcr ? 'outline-secondary' : 'secondary'}
            onClick={() => {
              // Already on digital form — do not re-fetch (would wipe progress).
              if (!isUsingOcr) return;
              // OCR path but program has no OCR model — switch without modal.
              if (!ocrAvailable) {
                handleChangeToDigitalForm();
                return;
              }
              toggleApplicationFormTypeWarningModal();
            }}
          >
            Fill out a Digital Form
          </Button>
        </Col>
      </Row>
      {!ocrAvailable && (
        <Alert variant="info" className="mb-24">
          OCR upload is not configured for this program. Continue with the digital
          form to enter application data manually.
        </Alert>
      )}
      {onTestMode() && (
        <Alert variant="info" hidden={!isUsingOcr}>
          <Alert.Heading>OCR is undergoing constant improvement</Alert.Heading>
          <p>
            The more applications are processed, the better the model will be at
            extracting data from the applications. As of right now, checkboxes
            require fine tuning.
          </p>
        </Alert>
      )}
      <Row>
        <Col>
          <Table hidden={applicationTemplates.length <= 0}>
            <thead>
              <tr className="font-weight-bold">
                <th style={{ width: isUsingOcr ? '40%' : '80%' }}>Document</th>
                <th hidden={!isUsingOcr} style={{ width: '20%' }}>
                  Attach
                </th>
                <th hidden={!isUsingOcr} style={{ width: '20%' }}>
                  File
                </th>
                <th style={{ width: '20%' }}>Download</th>
              </tr>
            </thead>
            {applicationTemplates.map((applicationTemplates, index) => (
              <tbody key={index}>
                <tr>
                  <td>{applicationTemplates.name}</td>
                  <td hidden={!isUsingOcr}>
                    <Button
                      disabled={ocrFileIsProcessing}
                      size="sm"
                      className="rounded"
                      variant="outline-primary"
                      onClick={() =>
                        submitApplicationSignal.update({
                          ocrModalVisible: true,
                          targetDocumentName: applicationTemplates.id,
                        })
                      }
                    >
                      {ocrFile || ocrFileSignedUrl
                        ? 'Replace'
                        : 'Attach and Process'}
                    </Button>
                  </td>
                  <td hidden={!isUsingOcr}>
                    {ocrFile || ocrFileSignedUrl ? (
                      <>
                        <FilePreviewButton
                          render={(submitting, f) => (
                            <Button
                              hidden={
                                !isFileTypeValidForPreview(ocrFile?.type) &&
                                !isFileTypeValidForPreview(ocrFileType)
                              }
                              size="sm"
                              className="rounded"
                              variant="outline-dark"
                              onClick={() =>
                                f(
                                  ocrFileSignedUrl
                                    ? {
                                        filePath: ocrFileSignedUrl,
                                        parentId: selectedDraft.id,
                                        uuid: ocrFileUuid,
                                        fileType: ocrFileType,
                                      }
                                    : ocrFile,
                                  'applicationDocuments',
                                  ocrFileType
                                )
                              }
                            >
                              Preview
                            </Button>
                          )}
                        />
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-info"
                      className="rounded"
                      onClick={() =>
                        handleDownloadDocument(applicationTemplates.id)
                      }
                    >
                      Download Template <FontAwesomeIcon icon={downloadIcon} />
                    </Button>
                  </td>
                </tr>
              </tbody>
            ))}
          </Table>
        </Col>
      </Row>
      <Row hidden={!ocrFileIsProcessing}>
        <Col>
          <small>Please wait while your application is processed.</small>
          <ProgressBar animated now={100} />
        </Col>
      </Row>
      {Object.keys(parsedOcrData).length > 0 && (
        <Row>
          <Col>
            <Alert hidden={ocrIntakeAlert !== 'partial'} variant="info">
              <p className="mb-0">
                Only part of the application was uploaded. We kept the fields we
                could read—review them below, fill in the rest, and click Confirm
                before continuing.
              </p>
            </Alert>
            <Alert hidden={ocrIntakeAlert !== 'badScan'} variant="warning">
              <div className="d-flex flex-row align-items-center">
                <FontAwesomeIcon
                  className="mr-24"
                  size="2x"
                  icon={warningIcon}
                />
                <p className="pr-16 mb-0">
                  We could not read much from this document. You can still fill
                  out the fields below, or try uploading a clearer scan. If you
                  upload a new document, use the template provided.
                </p>
                <Button
                  variant="outline-dark"
                  className="rounded"
                  size="sm"
                  onClick={() =>
                    submitApplicationSignal.update({
                      ocrIntakeAlert: 'none',
                    })
                  }
                >
                  Dismiss
                </Button>
              </div>
            </Alert>
            <OCRRender />
          </Col>
        </Row>
      )}
      <Row className="my-64">
        <Col className="text-center">
          <Button className="rounded" onClick={prevStep}>
            Prev Page
          </Button>
        </Col>
        <Col className="text-center">
          <Button className="rounded" onClick={nextStep}>
            Next Page
          </Button>
        </Col>
      </Row>
      <ModalWrapper
        centered
        size="xl"
        show={ocrModalVisible}
        onHide={() =>
          submitApplicationSignal.update({ ocrModalVisible: false })
        }
      >
        <Modal.Header>
          <Modal.Title>OCR Processing</Modal.Title>
          <CloseModalButton
            onCloseModal={() =>
              submitApplicationSignal.update({ ocrModalVisible: false })
            }
          />
        </Modal.Header>
        <Modal.Body>
          <p>
            We will use OCR to read your document and show the fields below for
            review. You may upload the full application or only completed
            sections—the rest can be filled in digitally.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="mr-8 rounded"
            variant="outline-secondary"
            onClick={() =>
              submitApplicationSignal.update({ ocrModalVisible: false })
            }
          >
            Cancel
          </Button>
          <input
            type="file"
            hidden
            name={targetDocumentName}
            id={`file-uploader-hidden-input-${targetDocumentName}`}
            onChange={(e) => {
              handleAttachFileForOcr(e.target.name, e.target.files[0]);
              submitApplicationSignal.update({
                ocrModalVisible: false,
              });
            }}
          />
          <Button
            className="rounded"
            variant="outline-primary"
            onClick={() =>
              document
                .getElementById(
                  `file-uploader-hidden-input-${targetDocumentName}`
                )
                .click()
            }
          >
            Upload
          </Button>
        </Modal.Footer>
      </ModalWrapper>
    </>
  );
};

export default SubmitApplicationsOCR;
